import React, { useState, useEffect } from 'react';
import { UserPersona } from '../OnboardingWizard';
import { searchTenants } from '../../portal/services/portalService';
import { TenantListingCard } from '../../../core/types/portal';
import { useNavigate } from 'react-router-dom';

import { createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, setDoc, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../../../core/services/firebase/config';
interface HandleClaimProps {
  persona: UserPersona;
  onComplete: () => void;
  onBack: () => void;
}

export const HandleClaim: React.FC<HandleClaimProps> = ({ persona, onComplete, onBack }) => {
  const [handle, setHandle] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [foundBusiness, setFoundBusiness] = useState<TenantListingCard | null>(null);
  
  // Registration Fields
  const [showRegForm, setShowRegForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [whatsapp, setWhatsapp] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  
  const navigate = useNavigate();

  // Real Firestore availability check
  const checkAvailability = async (checkHandle: string): Promise<boolean> => {
    try {
      const q = query(collection(db, 'users'), where('handle', '==', checkHandle));
      const snapshot = await getDocs(q);
      return snapshot.empty;
    } catch (error) {
      console.error("Error checking handle availability:", error);
      // Fail safe: assume taken if error to prevent overwrite/duplicates
      return false;
    }
  };

  const generateSuggestions = (baseHandle: string) => {
    return [
      `${baseHandle}_br`,
      `${baseHandle}${Math.floor(Math.random() * 99)}`,
      `sou_${baseHandle}`
    ];
  };

  const handleSearchChange = async (val: string) => {
    const sanitized = val.toLowerCase().replace(/[^a-z0-9_.]/g, '');
    setHandle(sanitized);
    setError(null);
    setSuggestions([]);
    setFoundBusiness(null);
    setShowRegForm(false); // Reset form if handle changes

    // Business Logic: Search specifically for businesses to claim
    if (persona === 'business' && sanitized.length > 2) {
       // Debounce this in production
       try {
         const results = await searchTenants({ query: sanitized, tenantTypes: ['pousada', 'hotel', 'loja', 'barco-hotel', 'guia'] });
         
         // Fuzzy match check (simplistic for now)
         const match = results.find(t => 
             t.businessName.toLowerCase().replace(/\s/g,'').includes(sanitized) || 
             (t.slug && t.slug.includes(sanitized))
         );
         
         if (match) {
             setFoundBusiness(match);
         }
       } catch (err) {
         console.error("Error searching business match", err);
       }
    }
  };

  const initRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!handle || handle.length < 3) {
      setError('Mínimo de 3 caracteres');
      return;
    }

    setIsChecking(true);
    setError(null);
    setSuggestions([]);

    setTimeout(async () => {
      // 1. Check Availability
      const isAvailable = await checkAvailability(handle);
      
      if (!isAvailable) {
        setIsChecking(false);
        setError('Este nome de usuário já está em uso.');
        setSuggestions(generateSuggestions(handle));
        return;
      }

      // 2. Success - Show Form
      setIsChecking(false);
      setShowRegForm(true);
    }, 1000);
  };



// ... existing imports ...

// Inside HandleClaim component...

  const handleFinalSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      
      setIsRegistering(true);
      setError(null);

      try {
        let user;

        // 1. Try to create user or sign in if exists
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            user = userCredential.user;
        } catch (createErr: any) {
            if (createErr.code === 'auth/email-already-in-use') {
                try {
                    const credential = await signInWithEmailAndPassword(auth, email, password);
                    user = credential.user;
                } catch (signInErr) {
                    throw createErr;
                }
            } else {
                throw createErr;
            }
        }

        if (!user) throw new Error('Falha ao autenticar usuário.');

        // 2. Update Profile (Non-blocking)
        try {
            await updateProfile(user, {
                displayName: handle,
                photoURL: null 
            });
        } catch (profileErr) {
            console.warn("Failed to update profile name", profileErr);
        }

        // 3. Create Firestore User Document
        // SAFETY: Firestore crashes if fields are undefined. Using || null or defaults.
        await setDoc(doc(db, 'users', user.uid), {
          uid: user.uid,
          handle: handle || 'user',
          email: email || '',
          displayName: handle || 'User',
          persona: persona || 'fisherman', // Default fallback
          contactWhatsapp: whatsapp || null,
          role: 'user', 
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          onboardingCompleted: true,
          setupStatus: 'completed',
          settings: {
             notifications: true,
             publicProfile: true
          }
        });

        // 4. Success State
        localStorage.setItem('devMode', 'true');
        setRegisterSuccess(true);

      } catch (err: any) {
        console.error("Registration Error:", err);
        
        if (err.code === 'auth/email-already-in-use') {
             setError('Este email já está sendo usado. Tente fazer login ou recuperar senha.');
        } else if (err.code === 'auth/weak-password') {
             setError('A senha é muito fraca. Tente uma senha mais forte.');
        } else if (err.code === 'auth/invalid-email') {
             setError('O formato do email é inválido.');
        } else if (err.code === 'permission-denied') {
             setError('Erro de permissão ao salvar dados. Tente novamente.');
        } else {
             // Show the actual error message for clearer debugging
             setError('Erro: ' + (err.message || 'Falha desconhecida.'));
        }
      } finally {
        setIsRegistering(false);
      }
  };

  const handleClaimExisting = () => {
    if (foundBusiness) {
        // Redirect to the simulated magic link flow for this business
        navigate(`/invite/CLAIM-${foundBusiness.id}`);
    }
  };

  const getPersonaLabel = () => {
    switch(persona) {
        case 'business': return 'sua empresa';
        case 'influencer': return 'seu canal';
        default: return 'você';
    }
  };

  return (
    <div className="w-full animate-fade-in text-center flex flex-col items-center pb-12">
      <button onClick={onBack} className="absolute top-0 left-0 p-4 text-white/40 hover:text-white transition-colors z-50">
        <span className="material-symbols-outlined">arrow_back</span>
      </button>

      <div className="mb-6 mt-2 relative z-10">
         <div className="w-16 h-16 mx-auto rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-4 text-3xl shadow-lg relative">
           🚀
           <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-brand-blue-DEFAULT rounded-full flex items-center justify-center text-[10px]">
             @
           </div>
         </div>
         <h2 className="text-2xl font-display font-bold text-white">
            {registerSuccess ? 'Tudo Pronto!' : showRegForm ? 'Aquecimento...' : 'Vamos criar sua identidade'}
         </h2>
         <p className="text-white/60 text-sm mt-2 max-w-xs mx-auto">
            {registerSuccess ? 'Sua conta foi criada com sucesso.' : showRegForm ? `Finalize seu cadastro para garantir @${handle}` : `Como ${getPersonaLabel()} será encontrado(a)?`}
         </p>
      </div>

      <div className="w-full max-w-xs relative z-10">
        {/* Helper Wrapper for Form Stages */}
        
        {!showRegForm ? (
            /* STAGE 1: HANDLE CHECK */
            <form onSubmit={initRegistration} className="space-y-6">
                <div className="relative group text-left">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <span className="text-brand-blue-DEFAULT font-bold text-lg">@</span>
                </div>
                
                <input
                    type="text"
                    value={handle}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder="seu.nome"
                    className={`w-full bg-[#0F172A] border-2 rounded-xl py-4 pl-10 pr-4 text-xl text-white placeholder-white/20 focus:outline-none transition-all font-mono tracking-wide
                        ${error ? 'border-brand-orange-DEFAULT' : 'border-white/10 focus:border-brand-blue-DEFAULT'}
                    `}
                    autoFocus
                />
                </div>

                {/* Business Match Found Card */}
                {foundBusiness && !error && (
                    <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-left animate-fade-in-up hover:bg-white/10 transition-colors cursor-pointer" onClick={handleClaimExisting}>
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-full bg-white/10 overflow-hidden">
                                {foundBusiness.coverImage ? (
                                    <img src={foundBusiness.coverImage} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <span className="material-symbols-outlined w-full h-full flex items-center justify-center text-white/50">store</span>
                                )}
                            </div>
                            <div>
                                <p className="text-xs text-brand-gold font-bold uppercase tracking-wider">Empresa Encontrada</p>
                                <p className="font-bold text-white leading-tight">{foundBusiness.businessName}</p>
                            </div>
                        </div>
                        <button 
                            type="button"
                            className="w-full py-2 bg-brand-gold text-brand-midnight text-sm font-bold rounded-lg hover:brightness-110"
                        >
                            Reivindicar Perfil
                        </button>
                    </div>
                )}

                {/* Suggestions */}
                {error && suggestions.length > 0 && (
                    <div className="animate-fade-in-up">
                        <p className="text-white/40 text-xs mb-2">Sugestões disponíveis:</p>
                        <div className="flex flex-wrap gap-2 justify-center">
                            {suggestions.map(sug => (
                                <button
                                    key={sug}
                                    type="button"
                                    onClick={() => { setHandle(sug); setError(null); setSuggestions([]); }}
                                    className="px-3 py-1.5 rounded-full bg-white/10 border border-white/5 text-sm font-mono text-brand-blue-light hover:bg-brand-blue-DEFAULT/20 hover:border-brand-blue-DEFAULT transition-all text-white"
                                >
                                    @{sug}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {error && !suggestions.length && (
                    <p className="text-brand-orange-DEFAULT text-sm font-medium animate-pulse">{error}</p>
                )}

                <div className="pt-2">
                    <button
                    type="submit"
                    disabled={isChecking || !handle}
                    className={`w-full py-4 rounded-xl font-bold text-lg shadow-lg transition-all transform active:scale-95 flex items-center justify-center gap-2
                    ${isChecking 
                        ? 'bg-white/10 text-white/50 cursor-wait' 
                        : 'bg-brand-blue-DEFAULT hover:bg-brand-blue-dark text-white shadow-glow-gold'
                    }`}
                    >
                    {isChecking ? (
                        <>
                            <span className="material-symbols-outlined animate-spin text-xl">progress_activity</span>
                            Verificando...
                        </>
                    ) : foundBusiness ? (
                        'Criar Novo (Ignorar)'
                    ) : (
                        'Continuar'
                    )}
                    </button>
                    <p className="text-xs text-white/30 mt-4 max-w-xs mx-auto">
                        Ao continuar, você concorda com nossos Termos de Uso.
                    </p>
                </div>
            </form>
        ) : registerSuccess ? (
            /* STAGE 3: CONFIRMATION (New) */
            <div className="animate-fade-in-up space-y-8 py-4">
                <div className="relative">
                    <div className="absolute inset-0 bg-brand-gold/20 blur-[40px] rounded-full animate-pulse-slow"></div>
                    <div className="w-24 h-24 mx-auto bg-gradient-to-br from-brand-gold to-brand-orange-DEFAULT rounded-full flex items-center justify-center text-5xl shadow-2xl border-4 border-[#0F172A] relative z-10">
                        ✨
                    </div>
                </div>
                
                <div className="space-y-2">
                    <h3 className="text-xl font-bold text-white">Bem-vindo(a)!</h3>
                    <p className="text-white/60">
                        Você garantiu o handle <span className="text-brand-gold font-mono font-bold">@{handle}</span>
                    </p>
                </div>

                <button
                    onClick={onComplete}
                    className="w-full py-4 bg-white text-gray-900 font-black text-lg rounded-xl shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2"
                >
                    Acessar Plataforma
                    <span className="material-symbols-outlined">arrow_forward</span>
                </button>
            </div>
        ) : (
            /* STAGE 2: FULL REGISTRATION */
            <form onSubmit={handleFinalSubmit} className="space-y-4 animate-fade-in-right">
                 {/* Confirm Handle Readonly */}
                 <div className="text-left mb-4 px-2">
                    <label className="text-xs font-bold text-white/40 uppercase tracking-widest">Seu Handle</label>
                    <div className="text-xl font-mono text-brand-gold font-bold">@{handle}</div>
                 </div>

                 {/* Email Field */}
                 <div className="space-y-1 text-left">
                    <label className="text-[10px] font-bold text-white/40 uppercase tracking-widest px-1">Email (Obrigatório)</label>
                    <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="seu@email.com"
                        className="w-full bg-[#0F172A] border border-white/10 rounded-xl py-3 px-4 text-white placeholder-white/20 focus:outline-none focus:border-brand-gold transition-colors"
                    />
                 </div>

                 {/* Password Field */}
                 <div className="space-y-1 text-left">
                    <label className="text-[10px] font-bold text-white/40 uppercase tracking-widest px-1">Senha (Obrigatório)</label>
                    <div className="relative">
                        <input
                            type={showPassword ? "text" : "password"}
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            className="w-full bg-[#0F172A] border border-white/10 rounded-xl py-3 px-4 text-white placeholder-white/20 focus:outline-none focus:border-brand-gold transition-colors pr-12"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                        >
                            <span className="material-symbols-outlined text-lg">
                                {showPassword ? 'visibility_off' : 'visibility'}
                            </span>
                        </button>
                    </div>
                    <p className="text-[10px] text-white/40 px-1">
                        Mínimo de 8 caracteres. Use letras e números para maior segurança.
                    </p>
                 </div>

                 {/* WhatsApp Field (Optional) */}
                 <div className="space-y-1 text-left">
                    <label className="text-[10px] font-bold text-white/40 uppercase tracking-widest px-1">WhatsApp (Opcional)</label>
                    <input
                        type="tel"
                        value={whatsapp}
                        onChange={(e) => setWhatsapp(e.target.value)}
                        placeholder="(00) 00000-0000"
                        className="w-full bg-[#0F172A] border border-white/10 rounded-xl py-3 px-4 text-white placeholder-white/20 focus:outline-none focus:border-brand-gold transition-colors"
                    />
                 </div>

                 <div className="pt-4">
                    {error && (
                        <div className="mb-4 p-3 rounded-lg bg-red-500/20 border border-red-500/50 flex items-center gap-2 animate-pulse">
                            <span className="material-symbols-outlined text-red-500 text-sm">error</span>
                            <span className="text-xs text-red-200 font-medium text-left leading-tight">{error}</span>
                        </div>
                    )}
                    <button
                        type="submit"
                        disabled={isRegistering}
                        className={`w-full py-4 font-bold text-lg rounded-xl shadow-glow-gold hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2 bg-brand-gold text-brand-midnight`}
                    >
                        {isRegistering ? (
                            <>
                                <span className="material-symbols-outlined animate-spin text-xl">progress_activity</span>
                                Finalizando...
                            </>
                        ) : (
                            'Finalizar Cadastro'
                        )}
                    </button>
                    <button 
                        type="button" 
                        onClick={() => setShowRegForm(false)}
                        className="mt-4 text-xs font-bold text-white/40 hover:text-white uppercase tracking-wider"
                    >
                        Voltar
                    </button>
                 </div>
            </form>
        )}
      </div>
    </div>
  );
};
